import { approverAt } from './approverStore';
// Direct order tickets: the fields a ticket can change, the approvers, and the
// approval rules. Shared by the request modal (CreateTicketModal) and the
// review page (SupportTickets).

// Amendable fields as per API spec. 'select' fields are lookups (same sources as
// DirectOrderEditModal.js) — the request modal supplies their options.
export const AMENDABLE_FIELDS = {
  doDate: { label: 'Date', type: 'date' },
  doInitiative: { label: 'Initiative', type: 'select' },
  doOtherInitiative: { label: 'Other Initiative', type: 'text' },
  doJustification: { label: 'Justification', type: 'select' },
  doDetailedJustification: { label: 'Detailed Justification', type: 'textarea' },
  doSupplier: { label: 'Supplier', type: 'text' },
  doNameBriefDesc: { label: 'Name/Brief Description', type: 'textarea' },
  doBreakdown: { label: 'Breakdown', type: 'textarea' },
  doTimeframe: { label: 'Timeframe', type: 'text' },
  doCostPerItem: { label: 'Cost Per Item', type: 'number' },
  doTotalCost: { label: 'Total Cost', type: 'number' },
  doCallForQuiteOrInterest: { label: 'Call for Quote or Interest', type: 'text' },
  doTermsOfPayment: { label: 'Terms of Payment', type: 'text' },
  doRefNoAndDateOfWiningBidEtc: { label: 'Ref No. and Date of Winning Bid', type: 'text' },
  doFundsAvailability: { label: 'Funds Availability', type: 'text' },
  doBriefHistory: { label: 'Brief History', type: 'textarea' },
  lineVoteId: { label: 'Line Vote', type: 'select' },
};

export const fieldLabel = (key) => AMENDABLE_FIELDS[key]?.label || key;

// Ticket approvals follow the direct order chain, strictly in order: amendments need all three
// levels, deletions only the first. The people come from the backend (approverStore and each
// ticket's approvalSteps), never from this file.
export { isApproverEmail } from './approverStore';

export const TICKET_LEVELS = [
  { key: 'ticket_firstApproval', level: 1, label: 'First approval' },
  { key: 'ticket_secondApproval', level: 2, label: 'Second approval' },
  { key: 'ticket_thirdApproval', level: 3, label: 'Final approval' },
];

const levelsFor = (ticket) => (ticket?.ticket_type === 'delete' ? TICKET_LEVELS.slice(0, 1) : TICKET_LEVELS);

/** Steps with state (approved | rejected | current | waiting) and the person for each level. */
export const getTicketSteps = (ticket) => {
  const levels = levelsFor(ticket);
  if (Array.isArray(ticket?.approvalSteps) && ticket.approvalSteps.length === levels.length) {
    return ticket.approvalSteps.map((s, i) => ({ ...levels[i], ...s, key: levels[i].key, level: levels[i].level }));
  }
  let blocked = false;
  return levels.map((lvl) => {
    const value = Number(ticket?.[lvl.key] || 0);
    let state;
    if (value === 4) {
      state = 'rejected';
      blocked = true;
    } else if (value === 1 && !blocked) {
      state = 'approved';
    } else if (blocked || ticket?.status !== 'open') {
      state = 'waiting';
    } else {
      state = 'current';
      blocked = true;
    }
    const person = approverAt(lvl.level);
    return { ...lvl, state, name: person?.name || lvl.label, title: person?.title || null, email: person?.email || null };
  });
};

export const getApprovalStatus = (ticket) => {
  const approvals = [
    ticket.ticket_firstApproval,
    ticket.ticket_secondApproval,
    ticket.ticket_thirdApproval
  ];
  const approvedCount = approvals.filter(a => a === 1).length;
  const totalRequired = ticket.ticket_type === 'delete' ? 1 : 3;
  return { approvedCount, totalRequired };
};

/** Name of whoever the ticket is waiting for, or "Approved" when nothing is left. */
export const getNextApprover = (ticket) => {
  const next = getTicketSteps(ticket).find((s) => s.state === 'current');
  return next ? next.name : 'Approved';
};

/** Whether this user holds the level the open ticket is waiting at. */
export const canUserApprove = (ticket, userEmail) => {
  if (!userEmail || ticket.status !== 'open') return false;
  const next = levelsFor(ticket).find((lvl) => Number(ticket[lvl.key] || 0) !== 1);
  return Boolean(next) && approverAt(next.level)?.email === userEmail.toLowerCase();
};

export const TICKET_STATUS = {
  open: { label: 'In review', tone: 'warn' },
  approved: { label: 'Approved', tone: 'ok' },
  rejected: { label: 'Rejected', tone: 'bad' },
};

export const ticketStatus = (status) => TICKET_STATUS[status] || { label: status || '—', tone: 'muted' };
